import type { Mat4 } from "./view";
import type { PlacementPosition } from "./props";
import { PATCH_MM } from "../sim/collision";

/** One effect a map places, as `tools/build_placements.mjs` writes it. */
export interface PlacedEffect {
  readonly effect: string;
  readonly position: PlacementPosition;
  readonly yawDeg: number;
  readonly pitchDeg: number;
  readonly rollDeg: number;
  readonly patch: readonly [number, number];
}

/** `LOAD_SIZE_WIDTH` (`GameLib/MapOutdoor.h:20`): how many patches either side
 *  of the player's own an area stays updated and drawn. */
export const LOAD_SIZE_WIDTH = 1;

/** `CMapOutdoor::Update`'s terrain coordinate (`MapOutdoorUpdate.cpp:55-64`):
 *  the patch the player stands in, clamped to the map. */
export function aroundPatch(
  xMm: number,
  yMm: number,
  patchesX: number,
  patchesY: number,
): { readonly x: number; readonly y: number } {
  const clamp = (value: number, count: number): number =>
    Math.min(Math.max(Math.trunc(value / PATCH_MM), 0), count - 1);
  return { x: clamp(xMm, patchesX), y: clamp(Math.abs(yMm), patchesY) };
}

function inWindow(patch: readonly [number, number], centre: { x: number; y: number }): boolean {
  // `AssignTerrainPtr`'s reference rectangle (`MapOutdoorLoad.cpp:118-121`).
  return Math.abs(patch[0] - centre.x) <= LOAD_SIZE_WIDTH && Math.abs(patch[1] - centre.y) <= LOAD_SIZE_WIDTH;
}

/** Which placed effects come alive and which stop as the player moves.
 *  `CMapOutdoor::RenderEffect` updates and draws only the areas
 *  `AssignTerrainPtr` puts in the window around the player's patch
 *  (`MapOutdoorRender.cpp:338-349`); an area that leaves the window is queued
 *  for deletion with its effect instances (`MapOutdoorUpdate.cpp:875-940`), and
 *  one that enters is loaded with fresh ones (`Area.cpp:531-570`). */
export class PlacedEffectWindow {
  private readonly byPatch = new Map<string, number[]>();
  private centre: { x: number; y: number } | null = null;

  constructor(
    private readonly effects: readonly PlacedEffect[],
    private readonly patchesX: number,
    private readonly patchesY: number,
  ) {
    effects.forEach((effect, index) => {
      const key = `${effect.patch[0]},${effect.patch[1]}`;
      const list = this.byPatch.get(key);
      if (list === undefined) this.byPatch.set(key, [index]);
      else list.push(index);
    });
  }

  /** The placements that entered and left the window since the last call,
   *  both in placement order. */
  update(xMm: number, yMm: number): { entered: number[]; left: number[] } {
    const next = aroundPatch(xMm, yMm, this.patchesX, this.patchesY);
    const previous = this.centre;
    if (previous !== null && previous.x === next.x && previous.y === next.y) {
      return { entered: [], left: [] };
    }
    this.centre = next;
    const entered: number[] = [];
    const left: number[] = [];
    this.effects.forEach((effect, index) => {
      const now = inWindow(effect.patch, next);
      const before = previous !== null && inWindow(effect.patch, previous);
      if (now && !before) entered.push(index);
      else if (before && !now) left.push(index);
    });
    return { entered, left };
  }

  /** Every placement in the window now, in placement order. */
  get live(): number[] {
    const centre = this.centre;
    if (centre === null) return [];
    const out: number[] = [];
    for (let y = centre.y - LOAD_SIZE_WIDTH; y <= centre.y + LOAD_SIZE_WIDTH; y += 1) {
      for (let x = centre.x - LOAD_SIZE_WIDTH; x <= centre.x + LOAD_SIZE_WIDTH; x += 1) {
        out.push(...(this.byPatch.get(`${x},${y}`) ?? []));
      }
    }
    return out.sort((a, b) => a - b);
  }
}

/** What one converted effect costs a field: its emitters, the most particles
 *  those emitters may hold at once, and its mesh groups. */
export interface EffectCost {
  readonly emitters: number;
  readonly particles: number;
  readonly meshes: number;
}

/** The most emitters, particles and mesh groups any one window of this map
 *  holds, each counted over its own busiest window: the size a field has to
 *  be for no placed effect to be evicted to make room for another. */
export function windowCapacity(
  effects: readonly PlacedEffect[],
  patchesX: number,
  patchesY: number,
  costOf: (effect: string) => EffectCost | null,
): EffectCost {
  const perPatch = new Map<string, { emitters: number; particles: number; meshes: number }>();
  for (const effect of effects) {
    const cost = costOf(effect.effect);
    if (cost === null) continue;
    const key = `${effect.patch[0]},${effect.patch[1]}`;
    const sum = perPatch.get(key) ?? { emitters: 0, particles: 0, meshes: 0 };
    sum.emitters += cost.emitters;
    sum.particles += cost.particles;
    sum.meshes += cost.meshes;
    perPatch.set(key, sum);
  }
  const best = { emitters: 0, particles: 0, meshes: 0 };
  for (let cy = 0; cy < patchesY; cy += 1) {
    for (let cx = 0; cx < patchesX; cx += 1) {
      const window = { emitters: 0, particles: 0, meshes: 0 };
      for (let y = cy - LOAD_SIZE_WIDTH; y <= cy + LOAD_SIZE_WIDTH; y += 1) {
        for (let x = cx - LOAD_SIZE_WIDTH; x <= cx + LOAD_SIZE_WIDTH; x += 1) {
          const sum = perPatch.get(`${x},${y}`);
          if (sum === undefined) continue;
          window.emitters += sum.emitters;
          window.particles += sum.particles;
          window.meshes += sum.meshes;
        }
      }
      best.emitters = Math.max(best.emitters, window.emitters);
      best.particles = Math.max(best.particles, window.particles);
      best.meshes = Math.max(best.meshes, window.meshes);
    }
  }
  return best;
}

type Row = readonly [number, number, number];

function times(a: readonly Row[], b: readonly Row[]): Row[] {
  return [0, 1, 2].map((i) => [0, 1, 2].map((j) => a[i][0] * b[0][j] + a[i][1] * b[1][j] + a[i][2] * b[2][j]) as unknown as Row);
}

/** `D3DXMatrixRotationYawPitchRoll` (`Area.cpp:548-552`) as the column-major
 *  3x3 that carries an effect's own local axes into this tree's world: roll
 *  about z, then pitch about x, then yaw about y, in D3DX's row-vector order,
 *  and then the same y mirror every placement takes (`props.ts`'s
 *  `modelMatrix`), because a map stores y negated. */
export function effectBasis(yawDeg: number, pitchDeg: number, rollDeg: number): Float64Array {
  const rad = Math.PI / 180;
  const [cy, sy] = [Math.cos(yawDeg * rad), Math.sin(yawDeg * rad)];
  const [cp, sp] = [Math.cos(pitchDeg * rad), Math.sin(pitchDeg * rad)];
  const [cr, sr] = [Math.cos(rollDeg * rad), Math.sin(rollDeg * rad)];
  const roll: Row[] = [[cr, sr, 0], [-sr, cr, 0], [0, 0, 1]];
  const pitch: Row[] = [[1, 0, 0], [0, cp, sp], [0, -sp, cp]];
  const yaw: Row[] = [[cy, 0, -sy], [0, 1, 0], [sy, 0, cy]];
  const m = times(times(roll, pitch), yaw);
  const out = new Float64Array(9);
  for (let axis = 0; axis < 3; axis += 1) {
    out[axis * 3] = m[axis][0];
    out[axis * 3 + 1] = -m[axis][1];
    out[axis * 3 + 2] = m[axis][2];
  }
  return out;
}

/** `CCullingManager::VisibilityCallback` (`EterLib/CullingManager.cpp:47-71`):
 *  an instance is hidden only when its bounding sphere is wholly outside the
 *  view frustum, and shown when it is inside or straddles a plane. The planes
 *  are the view-projection matrix's own rows. */
export function sphereOutsideFrustum(
  viewProj: Mat4 | Float32Array,
  xMm: number,
  yMm: number,
  zMm: number,
  radiusMm: number,
): boolean {
  const m = viewProj;
  for (let axis = 0; axis < 3; axis += 1) {
    for (const sign of [1, -1]) {
      const a = m[3] + sign * m[axis];
      const b = m[7] + sign * m[4 + axis];
      const c = m[11] + sign * m[8 + axis];
      const d = m[15] + sign * m[12 + axis];
      const length = Math.hypot(a, b, c);
      if (length === 0) continue;
      if ((a * xMm + b * yMm + c * zMm + d) / length < -radiusMm) return true;
    }
  }
  return false;
}

/** `CEffectInstance::GetBoundingSphere` (`EffectInstance.cpp:234-239`): the
 *  sphere the culling manager tests, at the placement's own translation plus
 *  the file's sphere offset, unrotated, its y mirrored as every placement's is.
 *  Null where the file's radius is zero: `SetEffectDataPointer` registers no
 *  sphere then (`EffectInstance.cpp:202-206`), and the instance stays shown. */
export function cullingSphere(
  effect: PlacedEffect,
  def: { readonly boundingRadiusMm: number; readonly boundingPositionMm?: readonly number[] },
): { xMm: number; yMm: number; zMm: number; radiusMm: number } | null {
  if (!(def.boundingRadiusMm > 0)) return null;
  const offset = def.boundingPositionMm ?? [0, 0, 0];
  return {
    xMm: effect.position.xMm + offset[0],
    yMm: effect.position.yMm - offset[1],
    zMm: effect.position.zMm + offset[2],
    radiusMm: def.boundingRadiusMm,
  };
}
